import { WButton, type ButtonLookProps } from "@/components/button/WButton.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import type { AddPaymentMethodModalProps } from "@/modules/billing/choose_method/AddPaymentMethodModal.tsx";
import type { PaymentModalProps } from "@/modules/billing/payment/PaymentModal.tsx";
import type { PayableInvoiceId } from "@/modules/billing/payment/PaymentParams.ts";
import { Trans } from "@lingui/react/macro";
import React from "react";

const AddPaymentMethodModal = React.lazy(() => import("@/modules/billing/choose_method/AddPaymentMethodModal.tsx"));
const PaymentModal = React.lazy(() => import("@/modules/billing/payment/PaymentModal.tsx"));

export interface AccountButtonProps extends Omit<ButtonLookProps, "label" | "isDisabled" | "icon"> {
    account: EBillingAccount;
}

interface PaymentModalButtonProps extends PaymentModalProps {
    button: React.ReactNode;
}

/** Modal wrapper that must be used for opening the top up (payment) modal. Applies workarounds to make payment controls work. */
function PaymentModalButton({ button, ...props }: PaymentModalButtonProps) {
    // TODO: Should the modal open out-of-context (ie. should it close when the parent component unmounts or the user navigates away)?
    return (
        <WModal
            // NB: Must disable focus trapping inside the payment modal since it breaks Razorpay (see issue WAR-5646)
            disableFocusManagement={true}
            button={button}
        >
            <PaymentModal {...props} />
        </WModal>
    );
}

export function TopUpNowButton({ children, account, ...props }: AccountButtonProps) {
    return (
        <PaymentModalButton
            account={account}
            button={
                <WButton
                    color="primary"
                    icon="jp-wallet-icon"
                    action={undefined}
                    isDisabled={!account.canTopUp}
                    {...props}
                >
                    {children ?? <Trans>Top up now</Trans>}
                </WButton>
            }
        />
    );
}

interface PayInvoiceButtonProps extends AccountButtonProps {
    invoice_id: PayableInvoiceId;
}

export function PayInvoiceButton({ children, account, invoice_id, ...props }: PayInvoiceButtonProps) {
    return (
        <PaymentModalButton
            account={account}
            invoice_id={invoice_id}
            button={
                <WButton variant="ghost" color="primary" icon="jp-card-icon" action={undefined} {...props}>
                    {children ?? <Trans comment="Button action: pay for unpaid invoice">Pay</Trans>}
                </WButton>
            }
        />
    );
}

interface AddPaymentMethodButtonProps extends AccountButtonProps, Pick<AddPaymentMethodModalProps, "onlyAutomated"> {}

export function AddPaymentMethodButton({ children, account, onlyAutomated, ...props }: AddPaymentMethodButtonProps) {
    return (
        <WModal
            // NB: react-aria focus trapping causes Stripe input elements (which are in an <iframe>) to break on Firefox - due to focus immediately being taken away from the element once you click in it, and also causing multiple inputs to be "focused" at once. (WAR-5846)
            disableFocusManagement={true}
            button={
                <WButton
                    isDisabled={!account.canAddPaymentMethod}
                    color="primary"
                    icon="jp-icon-add"
                    action={undefined}
                    {...props}
                >
                    {children ?? <Trans>Add payment method</Trans>}
                </WButton>
            }
        >
            <AddPaymentMethodModal account={account} onlyAutomated={onlyAutomated} />
        </WModal>
    );
}
